import React from "react";
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert,
} from "@mui/material";
import {
  People, MedicalServices, Assignment, LocalPharmacy,
  BarChart, Vaccines, Groups, Science,
} from "@mui/icons-material";
import { useGetAdminStatsQuery } from "state/api";
import foliregLogo  from "../../assets/Folireg.png";
import bisberryLogo from "../../assets/Bisberry.png";
import endocareLogo from "../../assets/Endocare.png";
import lycoredLogo  from "../../assets/Lycored-M.png";

/* ── Stat card ── */
const StatCard = ({ title, value, icon: Icon, color = "#9c27b0", bg = "#f3e5f5", logo }) => (
  <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: 2, background: bg,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon sx={{ color, fontSize: 26 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
          {logo && (
            <Box component="img" src={logo} alt=""
              sx={{ height: 16, width: "auto", objectFit: "contain", flexShrink: 0 }} />
          )}
          <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
        </Box>
        <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1 }}>{value}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { data: stats, isLoading, error } = useGetAdminStatsQuery();

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ mt: 2 }}>
      Failed to load dashboard data. Please refresh the page.
    </Alert>
  );

  /* Backend returns { totals: {...}, amLeaderboard: [...] } */
  const t = stats?.totals || {};
  const leaderboard = stats?.amLeaderboard || [];

  const cards = [
    { title: "Total MRs",        value: t.total_mrs            ?? 0, icon: People,          color: "#7b1fa2", bg: "#f3e5f5" },
    { title: "Total AMs",        value: t.total_ams            ?? 0, icon: Groups,          color: "#1565c0", bg: "#e3f2fd" },
    { title: "Rx Visits",        value: t.total_rx_visits      ?? 0, icon: MedicalServices, color: "#2e7d32", bg: "#e8f5e9", logo: foliregLogo },
    { title: "Total New Rx",     value: t.total_new_rx         ?? 0, icon: Assignment,      color: "#e65100", bg: "#fff3e0", logo: foliregLogo },
    { title: "Samples Given",    value: t.total_samples        ?? 0, icon: Vaccines,        color: "#6a1b9a", bg: "#ede7f6", logo: foliregLogo },
    { title: "Weekly Entries",   value: t.total_weekly         ?? 0, icon: BarChart,        color: "#0277bd", bg: "#e1f5fe", logo: bisberryLogo },
    { title: "PSP Entries",      value: t.psp_count            ?? 0, icon: LocalPharmacy,   color: "#ad1457", bg: "#fce4ec", logo: endocareLogo },
    { title: "PSP Kits Given",   value: t.total_psp_kits       ?? 0, icon: LocalPharmacy,   color: "#880e4f", bg: "#fce4ec", logo: endocareLogo },
    { title: "Lycored Entries",  value: t.total_lycored_visits ?? 0, icon: Science,         color: "#4527a0", bg: "#ede7f6", logo: lycoredLogo },
    { title: "POB Strips",       value: t.total_pob            ?? 0, icon: Assignment,      color: "#558b2f", bg: "#f1f8e9" },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Admin Dashboard</Typography>

      {/* ── Stat cards ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {cards.map((c) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={c.title}>
            <StatCard {...c} />
          </Grid>
        ))}
      </Grid>

      {/* ── AM Leaderboard ── */}
      {leaderboard.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} gutterBottom>AM Team Leaderboard</Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, background: "#f5f5f5" } }}>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>MgCd</TableCell>
                  <TableCell align="center">Team Size</TableCell>
                  <TableCell align="center">Total New Rx</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboard.map((am, i) => (
                  <TableRow key={am.MgCd} hover>
                    <TableCell>
                      {i < 3
                        ? <Chip label={`#${i + 1}`} size="small" color={i === 0 ? "warning" : i === 1 ? "default" : "default"} sx={{ fontWeight: 700 }} />
                        : <Typography variant="body2" color="text.secondary">#{i + 1}</Typography>
                      }
                    </TableCell>
                    <TableCell><Typography fontWeight={i < 3 ? 700 : 400}>{am.name}</Typography></TableCell>
                    <TableCell><Chip label={am.MgCd} size="small" variant="outlined" /></TableCell>
                    <TableCell align="center">{am.team_size}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} color="primary.main">{am.total_new_rx}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {leaderboard.length === 0 && !isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>No AM leaderboard data yet.</Alert>
      )}
    </Box>
  );
};

export default AdminDashboard;
